import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { reimbursementsAPI } from "../api/client";
import { mayPerform } from "../utils/userAccess";
import "./ExpenseReimbursement.css";

const emptyLine = () => ({
  groupName: "",
  natureName: "",
  expenseCode: "",
  expenseDate: "",
  invoiceNumber: "",
  invoiceDate: "",
  amount: "",
  attach: null,
  file: null,
});

const STATUS_LABELS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  UNDER_REVIEW: "Under Review",
  PENDING_FINANCE_VERIFICATION: "Pending Finance Approval",
  RETURNED_FOR_CORRECTION: "Returned for Correction",
  "Pending Project Approval": "Pending Project Approval",
  "Pending Finance Approval": "Pending Finance Approval",
  "Pending Payment": "Pending Payment",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  PROCESSED: "Processed",
  PAID: "Paid",
};

// Owner claimable statuses (Project Owner, stage 1).
const CLAIMABLE = new Set(["SUBMITTED", "UNDER_REVIEW"]);
// Finance claimable status (Finance verification, stage 2).
const FINANCE_CLAIMABLE = new Set(["PENDING_FINANCE_VERIFICATION"]);

function formatAmount(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return "—";
  const [datePart = "", timePart = ""] = String(value).split("T")[0].split(" ");
  const date = String(value).split(" ")[0] || datePart;
  const time = timePart && timePart.length >= 5 ? timePart.slice(0, 5) : "";
  return (date || "") + (time ? ` · ${time}` : "");
}

// Amounts can arrive as a number or as a numeric string (possibly with a rupee
// symbol, commas or spaces) — collapse them all to a number.
function parseAmount(value) {
  if (typeof value === "number") return Number(value) || 0;
  if (typeof value === "string") return Number(value.replace(/[₹,\s]/g, "")) || 0;
  return 0;
}

// Expense lines are stored as JSON — the API returns an array, but tolerate a
// JSON string (e.g. a stale cached detail) so the table never renders empty.
function toLineArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function classificationForBusinessUnit(value) {
  const normalized = String(value || "").trim().replace(/\s+/g, " ").replace(/\s*-\s*/g, "-").toUpperCase();
  if (normalized.startsWith("COR-")) return "COR";
  if (normalized.startsWith("SGA SUPPORT-")) return "SGA";
  return "";
}

function StatusBadge({ status }) {
  const cls = `reimbursement-status reimbursement-status--${String(status || "").toLowerCase().replace(/[_ ]/g, "-")}`;
  return <span className={cls}>{STATUS_LABELS[status] || status || "—"}</span>;
}

export default function ExpenseReimbursement() {
  const canCreate = mayPerform("reimbursement", "create");
  const canApprove = mayPerform("reimbursement", "approve");
  const canReject = mayPerform("reimbursement", "reject");
  const canVerify = mayPerform("reimbursement", "verify");
  const canProcess = mayPerform("reimbursement", "process");
  const canViewDashboard = mayPerform("reimbursement_dashboard", "view");
  const canViewApproved = mayPerform("reimbursement_approved", "view");
  const showApprovalsTab = canApprove || canReject;
  const showVerificationTab = canVerify;
  const showApprovedTab = canApprove || canReject || canVerify || canProcess || canViewApproved;

  const [tab, setTab] = useState("mine");
  const [profile, setProfile] = useState(null);
  // "loading" | "ready" | "no-profile". "no-profile" means the signed-in user has
  // no eligible row in headcount_employees — the page renders ONE warning for it.
  const [profileState, setProfileState] = useState("loading");
  const [mappings, setMappings] = useState([]);
  const [mappingDepartment, setMappingDepartment] = useState("");
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectOpen, setProjectOpen] = useState(false);
  const [projectFocusedIndex, setProjectFocusedIndex] = useState(0);
  const [lines, setLines] = useState([emptyLine()]);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [verification, setVerification] = useState([]);
  const [approvedByMe, setApprovedByMe] = useState([]);
  const [processing, setProcessing] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardFilters, setDashboardFilters] = useState({ dateRange: "Overall", basedOn: "submitted" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMode, setDetailMode] = useState("view");
  const [showReject, setShowReject] = useState(false);
  const [returningForCorrection, setReturningForCorrection] = useState(false);
  // Which stage owns the open reject / return modal: "owner" (Project Owner)
  // or "finance" (Finance verification). It decides which API is called.
  const [rejectScope, setRejectScope] = useState("owner");
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);

  const refreshMine = useCallback(() => reimbursementsAPI.getAll().then((r) => setMine(r.data)).catch(() => setMine([])), []);
  const refreshApprovals = useCallback(() => reimbursementsAPI.getApprovals().then((r) => setApprovals(r.data)).catch(() => setApprovals([])), []);
  const refreshVerification = useCallback(() => reimbursementsAPI.getVerificationQueue().then((r) => setVerification(r.data)).catch(() => setVerification([])), []);
  const refreshApprovedByMe = useCallback(() => reimbursementsAPI.getApprovedByMe().then((r) => setApprovedByMe(r.data)).catch(() => setApprovedByMe([])), []);
  const refreshProcessing = useCallback(() => reimbursementsAPI.getProcessingQueue().then((r) => setProcessing(r.data)).catch(() => setProcessing([])), []);
  const refreshDashboard = useCallback((filters = {}) => reimbursementsAPI.getDashboard(filters).then((r) => setDashboard(r.data)).catch(() => setDashboard(null)), []);

  useEffect(() => {
    reimbursementsAPI.getProfile()
      .then((p) => { setProfile(p.data); setProfileState("ready"); })
      .catch((e) => {
        // 404 = no eligible employee profile. The page body renders the single
        // warning for this case, so no toast is raised (it used to repeat the
        // message once per StrictMode mount on top of the inline warning).
        if (e?.response?.status === 404) { setProfileState("no-profile"); return; }
        setProfileState("ready");
        toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not load reimbursement details");
      });
  }, []);
  useEffect(() => { refreshMine(); }, [refreshMine]);
  useEffect(() => { if (showApprovalsTab) refreshApprovals(); }, [showApprovalsTab, refreshApprovals]);
  useEffect(() => { if (showVerificationTab) refreshVerification(); }, [showVerificationTab, refreshVerification]);
  useEffect(() => { if (showApprovedTab) refreshApprovedByMe(); }, [showApprovedTab, refreshApprovedByMe]);
  useEffect(() => { if (canProcess) refreshProcessing(); }, [canProcess, refreshProcessing]);
  useEffect(() => { if (tab === "dashboard") refreshDashboard(); }, [tab, refreshDashboard]);

  const department = classificationForBusinessUnit(project?.business_unit);

  // The expense nature list comes from the selected Department of Expense.
  useEffect(() => {
    setMappings([]);
    setMappingDepartment("");
    if (!department) return;
    let active = true;
    reimbursementsAPI.expenseMappings(department)
      .then((r) => { if (active) { setMappings(r.data); setMappingDepartment(department); } })
      .catch(() => { if (active) setMappings([]); });
    return () => { active = false; };
  }, [department]);

  useEffect(() => {
    if (!mappingDepartment || mappingDepartment !== department) return;
    setLines((old) => old.map((line) => {
      const groupIsValid = mappings.some((mapping) => mapping.groupName === line.groupName);
      if (!groupIsValid) return { ...line, groupName: "", natureName: "", expenseCode: "" };
      const selectedMapping = mappings.find((mapping) => mapping.groupName === line.groupName && mapping.natureName === line.natureName);
      if (!selectedMapping) return { ...line, natureName: "", expenseCode: "" };
      return { ...line, expenseCode: selectedMapping.code || "" };
    }));
  }, [mappings, mappingDepartment, department]);

  const updateLine = (index, key, value) => {
    setLines((old) => old.map((line, i) => {
      if (i !== index) return line;
      const updated = { ...line, [key]: value };
      if (key === "groupName") {
        updated.natureName = "";
        updated.expenseCode = "";
      }
      if (key === "natureName") {
        const match = mappings.find((m) => m.groupName === line.groupName && m.natureName === String(value));
        updated.expenseCode = match?.code || "";
      }
      return updated;
    }));
  };

  const pickFile = (index, file) => {
    setLines((old) => old.map((line, i) => (i === index ? { ...line, file: file || null, attach: file ? null : line.attach } : line)));
  };

  const selectProject = async (value) => {
    const selected = projects.find((p) => String(p.id) === String(value));
    if (!selected) { setProject(null); return; }
    // Re-fetch the full details from the projects table so the read-only
    // Project Details (Customer / Description / Delivery Name) are authoritative.
    try {
      const res = await reimbursementsAPI.getProject(selected.id);
      setProject(res.data);
    } catch (e) {
      setProject(selected);
    }
  };

  // Combined search + dropdown selection for the Project field. The dropdown is
  // genuinely one control: typing filters it by id/description, focusing it
  // shows every available project, and picking one stores its projects.id.
  const pickProject = (p) => {
    setProjectSearch(`${String(p.project_id || "").trim()} | ${String(p.description || "").trim()}`);
    setProjectOpen(false);
    selectProject(String(p.id));
  };

  const handleProjectKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!projectOptions.length) return;
      if (!projectOpen) { setProjectOpen(true); return; }
      setProjectFocusedIndex((i) => Math.min(i + 1, projectOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setProjectFocusedIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = projectOptions[projectFocusedIndex];
      if (projectOpen && opt) pickProject(opt);
    } else if (e.key === "Escape") {
      setProjectOpen(false);
    }
  };

  const payloadLines = (sourceLines) => {
    const failed = [];
    const made = sourceLines.map((line, i) => {
      if (!line.attach?.url) { failed.push(i + 1); return null; }
      return {
        groupName: line.groupName,
        natureName: line.natureName,
        expenseCode: line.expenseCode,
        expenseDate: line.expenseDate,
        invoiceNumber: line.invoiceNumber.trim(),
        invoiceDate: line.invoiceDate,
        amount: Number(line.amount),
        attachment: { url: line.attach.url, name: line.attach.name, type: line.attach.type || "" },
      };
    });
    if (failed.length) throw new Error(`Upload missing for expense line ${failed.join(", ")}`);
    return made;
  };

  const uploadPendingFiles = async () => {
    let updated = lines;
    for (let i = 0; i < updated.length; i += 1) {
      const file = updated[i].file;
      if (!file) continue;
      const formData = new FormData();
      formData.append("file", file);
      const res = await reimbursementsAPI.upload(formData);
      const { url, originalName, contentType } = res.data;
      const attach = { url, name: originalName, type: contentType || "" };
      setLines((old) => old.map((line, idx) => (idx === i ? { ...line, attach, file: null } : line)));
      updated = updated.map((line, idx) => (idx === i ? { ...line, attach, file: null } : line));
    }
    return updated;
  };

  // Every reimbursement (COR or SGA) must be charged to a customer project; the
  // Project Owner from the Projects master is the first approver.
  const projectRequired = true;

  // Load every available (In progress) project from the existing projects source
  // as soon as the form is in play, since a project is always required.
  useEffect(() => {
    if (!projectRequired) {
      setProject(null);
      setProjects([]);
      setProjectSearch("");
      return undefined;
    }
    let active = true;
    reimbursementsAPI.projects("")
      .then((r) => { if (active) setProjects(r.data); })
      .catch(() => { if (active) setProjects([]); });
    return () => { active = false; };
  }, [projectRequired]);

  const validateLines = () => {
    if (projectRequired && !project) return "Select a project for this reimbursement";
    if (projectRequired && !department) return "The selected project BU must start with COR- or SGA Support-";
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.natureName || !line.expenseCode || !line.groupName || !line.expenseDate ||
          !line.invoiceNumber.trim() || !line.invoiceDate ||
          !Number.isInteger(Number(line.amount)) || Number(line.amount) <= 0) {
        return `Complete all fields for expense line ${i + 1}`;
      }
      if (!line.attach?.url && !line.file) return `Attach a receipt for expense line ${i + 1}`;
    }
    return "";
  };

  const save = async (addOneMore = false) => {
    const validationError = validateLines();
    if (validationError) { toast.error(validationError); return null; }
    setBusy(true);
    try {
      const finalLines = await uploadPendingFiles();
      const data = { department, projectId: projectRequired && project ? project.id : null, lines: payloadLines(finalLines) };
      let id;
      if (editId) {
        await reimbursementsAPI.update(editId, data);
        id = editId;
        toast.success("Draft updated");
      } else {
        const res = await reimbursementsAPI.create(data);
        id = res.data.id;
        toast.success("Reimbursement draft saved");
      }
      setEditId(null);
      setLines([emptyLine()]);
      setProject(null);
      setProjectSearch("");
      setProjectOpen(false);
      refreshMine();
      setTab("mine");
      if (addOneMore) toast.info("Add the next expense line");
      return id;
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.error || e?.response?.data?.message || "Could not save draft");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (id, mode = "view") => {
    setDetailMode(mode);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await reimbursementsAPI.getById(id);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not load request");
    } finally {
      setDetailLoading(false);
    }
  };

  const submitDraft = async (id) => {
    setActing(true);
    try {
      await reimbursementsAPI.submit(id);
      toast.success("Request submitted for approval");
      if (detail?.id === id) setDetail(null);
      await refreshMine();
      if (showApprovalsTab) await refreshApprovals();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not submit request");
      return false;
    } finally { setActing(false); }
  };

  const saveAndSubmit = async () => {
    // Reuse the existing Save Draft handler (creates/updates with status DRAFT)
    // and the existing Submit for Approval handler to start the workflow —
    // no second submission API.
    const id = await save(false);
    if (!id) return;
    await submitDraft(id);
  };

  const editDraft = async (id) => {
    try {
      const res = await reimbursementsAPI.getById(id);
      const d = res.data;
      if (d.projectId) {
        let projectDetails;
        try {
          const projectResponse = await reimbursementsAPI.getProject(d.projectId);
          projectDetails = projectResponse.data;
        } catch {
          projectDetails = { id: d.projectId, project_id: d.project_code, description: d.project_description, end_customer: d.customer, project_owners_delivery: d.approver_name, project_owners_delivery_id: d.approver_id, nature: d.project_nature };
        }
        setProject(projectDetails);
        setProjectSearch(`${String(d.project_code || "").trim()} | ${String(d.project_description || "").trim()}`);
        setProjectOpen(false);
      } else {
        setProject(null);
      }
      setLines(toLineArray(d.lines).map((line) => {
        const attach = line.attachment || line.attachments?.[0] || null;
        return {
          groupName: line.groupName || "",
          natureName: line.natureName || "",
          expenseCode: line.expenseCode,
          expenseDate: line.expenseDate,
          invoiceNumber: line.invoiceNumber,
          invoiceDate: line.invoiceDate,
          amount: String(line.amount),
          attach,
          file: null,
        };
      }));
      setEditId(id);
      setDetail(null);
      setTab("form");
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not load draft");
    }
  };

  const backToMyRequests = () => {
    // Discard only the in-progress edit and return to the My Requests list.
    setEditId(null);
    setLines([emptyLine()]);
    setProject(null);
    setProjectSearch("");
    setProjectOpen(false);
    setTab("mine");
  };

  const review = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await reimbursementsAPI.review(detail.id);
      toast.success("Review started — request is under review");
      await openDetail(detail.id, detailMode);
      await refreshApprovals();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not start review");
    } finally { setActing(false); }
  };

  const approve = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await reimbursementsAPI.approve(detail.id);
      toast.success("Approved — sent for Finance verification");
      await openDetail(detail.id, detailMode);
      await refreshApprovals();
      if (showVerificationTab) await refreshVerification();
      await refreshApprovedByMe();
      await refreshMine();
      if (canProcess) await refreshProcessing();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not approve request");
    } finally { setActing(false); }
  };

  const reject = async () => {
    if (!detail || rejectReason.trim().length < 5) {
      return toast.error("Enter a rejection reason of at least 5 characters");
    }
    setActing(true);
    try {
      // Stage 2 (Finance) uses its own endpoint so the permission and the
      // history entry differ from the Project Owner rejection.
      if (rejectScope === "finance") {
        await reimbursementsAPI.financeReject(detail.id, rejectReason.trim());
      } else {
        await reimbursementsAPI.reject(detail.id, rejectReason.trim());
      }
      toast.success("Reimbursement rejected");
      setShowReject(false);
      setRejectReason("");
      await openDetail(detail.id, detailMode);
      await refreshApprovals();
      if (showVerificationTab) await refreshVerification();
      await refreshMine();
      await refreshApprovedByMe();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not reject request");
    } finally { setActing(false); }
  };

  const returnForCorrection = async () => {
    if (!detail || rejectReason.trim().length < 5) return toast.error("Enter a correction reason of at least 5 characters");
    setActing(true);
    try {
      if (rejectScope === "finance") {
        await reimbursementsAPI.financeReturn(detail.id, rejectReason.trim());
      } else {
        await reimbursementsAPI.returnForCorrection(detail.id, rejectReason.trim());
      }
      toast.success("Request returned to the employee for correction");
      setShowReject(false);
      setReturningForCorrection(false);
      setRejectReason("");
      await openDetail(detail.id, detailMode);
      await refreshApprovals();
      if (showVerificationTab) await refreshVerification();
      await refreshMine();
      await refreshApprovedByMe();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not return request");
    } finally { setActing(false); }
  };

  // Finance verification (stage 2): the claim is approved for payment.
  const verify = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await reimbursementsAPI.verify(detail.id);
      toast.success("Verified — reimbursement approved for payment");
      await openDetail(detail.id, detailMode);
      if (showVerificationTab) await refreshVerification();
      await refreshApprovedByMe();
      await refreshMine();
      if (canProcess) await refreshProcessing();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not verify request");
    } finally { setActing(false); }
  };

  const process = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await reimbursementsAPI.process(detail.id);
      toast.success("Reimbursement marked as processed");
      await openDetail(detail.id, detailMode);
      await refreshMine();
      await refreshApprovals();
      await refreshApprovedByMe();
      if (canProcess) await refreshProcessing();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not process request");
    } finally { setActing(false); }
  };

  const pay = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await reimbursementsAPI.pay(detail.id);
      toast.success("Reimbursement marked as paid");
      await openDetail(detail.id, detailMode);
      await refreshMine();
      await refreshApprovals();
      await refreshApprovedByMe();
      if (canProcess) await refreshProcessing();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not mark reimbursement as paid");
    } finally { setActing(false); }
  };

  const applyDashboardFilters = (filters) => { setDashboardFilters(filters); refreshDashboard(filters); };
  const exportExcel = async () => {
    try {
      const response = await reimbursementsAPI.exportExcel(dashboardFilters);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reimbursements.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.error || "Could not export reimbursements"); }
  };

  const downloadReceipt = async (lineId, originalName) => {
    if (!detail) return;
    try {
      const res = await reimbursementsAPI.getAttachment(detail.id, lineId);
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Could not open receipt");
    }
  };

  const total = useMemo(() => lines.reduce((s, line) => s + (Number(line.amount) || 0), 0), [lines]);

  // Distinct Expense Groups for the selected Department of Expense (the Expense
  // Nature dropdown is then filtered by the chosen group).
  const groupOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of mappings) {
      if (!seen.has(m.groupName)) { seen.add(m.groupName); out.push(m.groupName); }
    }
    return out;
  }, [mappings]);
  const naturesForGroup = (line) => mappings.filter((m) => m.groupName === line.groupName);

  // The Project combobox lists every available project (from the existing projects
  // API, never hardcoded). Typing filters it by project_id or description; an
  // empty/focused field shows all projects. When the input equals the selected
  // project's display text the filter is cleared so re-opening still lists all
  // options, and an already-selected project stays listable even if it is no
  // longer part of the loaded set (e.g. an older draft).
  const projectOptions = useMemo(() => {
    const selectedDisplay = project ? `${String(project.project_id || "").trim()} | ${String(project.description || "").trim()}` : "";
    const hasMatch = project != null && projectSearch.trim() === selectedDisplay.trim();
    const term = (hasMatch ? "" : projectSearch).trim().toLowerCase();
    let opts = term
      ? projects.filter((p) =>
          String(p.project_id || "").toLowerCase().includes(term) ||
          String(p.description || "").toLowerCase().includes(term))
      : projects;
    if (project && !opts.some((p) => String(p.id) === String(project.id))) {
      opts = [project, ...opts];
    }
    return opts;
  }, [projects, projectSearch, project]);

  // Reset the highlighted row whenever the visible option set changes.
  useEffect(() => { setProjectFocusedIndex(0); }, [projectOpen, projectOptions.length]);

  return <main className="reimbursement-page">
    <header className="reimbursement-header reimbursement-header--split">
      <div>
        <p className="reimbursement-eyebrow">FINANCE</p>
        <h1>Reimbursement</h1>
        <p>Raise business expenses against your project and track the approval journey.</p>
      </div>
      {canCreate && <button type="button" className="reimbursement-submit reimbursement-raise" onClick={() => setTab("form")}>+ Raise Request</button>}
    </header>

    <nav className="reimbursement-tabs">
      <button type="button" className={tab === "mine" ? "is-active" : ""} onClick={() => setTab("mine")}>My Requests</button>
      {canViewDashboard && <button type="button" className={tab === "dashboard" ? "is-active" : ""} onClick={() => setTab("dashboard")}>Dashboard</button>}
      {showApprovalsTab && <button type="button" className={tab === "approvals" ? "is-active" : ""} onClick={() => setTab("approvals")}>Approvals</button>}
      {showVerificationTab && <button type="button" className={tab === "verification" ? "is-active" : ""} onClick={() => { setTab("verification"); refreshVerification(); }}>Finance Verification</button>}
      {canProcess && <button type="button" className={tab === "process" ? "is-active" : ""} onClick={() => { setTab("process"); refreshProcessing(); }}>To Process</button>}
      {showApprovedTab && <button type="button" className={tab === "approved" ? "is-active" : ""} onClick={() => { setTab("approved"); refreshApprovedByMe(); }}>Approved</button>}
    </nav>

    {tab === "dashboard" && canViewDashboard && <ReimbursementDashboard summary={dashboard} filters={dashboardFilters} onApply={applyDashboardFilters} onExport={exportExcel} />}

    {profileState === "no-profile" && (
      <p className="reimbursement-state">This tab is exclusively available to employees.</p>
    )}

    {tab === "form" && canCreate && profile && (
      <div className="reimbursement-tab-body">
        {editId && (
          <div className="reimbursement-edit-header">
            <div>
              <h2>Edit Reimbursement Draft</h2>
              <p>Draft #{editId}</p>
            </div>
            <button type="button" className="reimbursement-edit-back" onClick={backToMyRequests}>Back to My Requests</button>
          </div>
        )}

        <section className="reimbursement-card">
          <h2>Employee Details</h2>
          <div className="reimbursement-grid">
            {[["Emp ID", profile.employee_id], ["Emp Name", profile.name], ["Company", profile.company], ["BU", profile.business_unit], ["Division", profile.division], ["Department", profile.department]].map(([label, value]) => (
              <label key={label}>{label}<input readOnly value={value || ""} /></label>
            ))}
          </div>
        </section>

        {projectRequired && (
          <section className="reimbursement-card">
            <h2>Project Details</h2>
            <label className="reimbursement-project">Project *<div className="reimbursement-combobox">
              <input
                type="text"
                value={projectSearch}
                onChange={(e) => { setProjectSearch(e.target.value); setProjectOpen(true); }}
                onFocus={() => setProjectOpen(true)}
                onBlur={() => setTimeout(() => setProjectOpen(false), 120)}
                onKeyDown={handleProjectKeyDown}
                placeholder="Search or select project..."
                autoComplete="off"
              />
              {projectOpen && (
                <div className="reimbursement-options reimbursement-options--project" style={{ height: projectOptions.length ? `${Math.min(projectOptions.length, 7) * 36}px` : "40px" }} onMouseDown={(e) => e.preventDefault()}>
                  {projectOptions.length ? projectOptions.map((p, idx) => (
                    <button type="button" key={String(p.id)} className={idx === projectFocusedIndex ? "is-active" : ""}
                      onMouseEnter={() => setProjectFocusedIndex(idx)}
                      onClick={() => pickProject(p)}>
                      {p.project_id || ""} | {p.description || ""}
                    </button>
                  )) : (
                    <div className="reimbursement-options-empty">
                      {projects.length ? `No projects match "${projectSearch.trim()}"` : "No available projects found."}
                    </div>
                  )}
                </div>
              )}
            </div><span className="reimbursement-hint">Search by Project ID or Project Description.</span></label>
            <div className="reimbursement-grid">
              <label>Project ID<input readOnly value={project?.project_id || ""} /></label>
              <label>Project Description<input readOnly value={project?.description || ""} /></label>
              <label>BU<input readOnly value={project?.business_unit || ""} /></label>
              <label>Expense Classification<input readOnly value={department || ""} /></label>
              <label>Customer<input readOnly value={project?.end_customer || ""} /></label>
              <label>Project Owner<input readOnly value={project?.project_owners_delivery || ""} /></label>
            </div>
          </section>
        )}

        <section className="reimbursement-card">
          <div className="reimbursement-section-head">
            <h2>Expense Details</h2>
            <button type="button" className="reimbursement-secondary" onClick={() => setLines((old) => [...old, emptyLine()])}>+ Add Expense Line</button>
          </div>
          {lines.map((line, i) => (
            <article className="reimbursement-line" key={i}>
              <div className="reimbursement-line-title">
                <h3>Expense Line {i + 1}</h3>
                {lines.length > 1 && <button type="button" onClick={() => setLines((old) => old.filter((_, j) => i !== j))}>Remove</button>}
              </div>
              <div className="reimbursement-grid">
                <label>Expense Group *<select value={line.groupName} onChange={(e) => updateLine(i, "groupName", e.target.value)} disabled={!department}>
                  <option value="">{department ? "Select group" : "Select a project with a supported BU first"}</option>
                  {groupOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                </select></label>
                <label>Expense Nature *<select value={line.natureName} onChange={(e) => updateLine(i, "natureName", e.target.value)} disabled={!line.groupName}>
                  <option value="">{line.groupName ? "Select nature" : "Select the Expense Group first"}</option>
                  {naturesForGroup(line).map((m) => <option key={m.code} value={m.natureName}>{m.code} · {m.natureName}</option>)}
                </select></label>
                <label>Expense Code<input readOnly value={line.expenseCode} placeholder="Auto" /></label>
                <label>Expense Date *<input type="date" value={line.expenseDate} onChange={(e) => updateLine(i, "expenseDate", e.target.value)} /></label>
                <label>Invoice Number *<input maxLength="120" value={line.invoiceNumber} onChange={(e) => updateLine(i, "invoiceNumber", e.target.value)} /></label>
                <label>Invoice Date *<input type="date" value={line.invoiceDate} onChange={(e) => updateLine(i, "invoiceDate", e.target.value)} /></label>
                <label>Amount Rs *<span className="reimbursement-amount"><span>₹</span><input type="number" min="1" step="1" value={line.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} /></span></label>
                <label>Attach Receipts / Invoice *<input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => pickFile(i, e.target.files?.[0] || null)} />
                  {line.attach && <span className="reimbursement-file-name">{line.attach.name}</span>}
                </label>
              </div>
            </article>
          ))}
          <div className="reimbursement-sub-total">Total: ₹{formatAmount(total)}</div>
          <div className="reimbursement-actions">
            <button disabled={busy} className="reimbursement-secondary" onClick={() => save(false)}>{busy ? "Saving…" : "Save as Draft"}</button>
            <button disabled={busy} onClick={saveAndSubmit}>{busy ? "Saving…" : "Save & Submit"}</button>
          </div>
        </section>
      </div>
    )}

    {tab === "mine" && (
      <div className="reimbursement-tab-body">
        <section className="reimbursement-card">
          <h2>My Requests</h2>
          <MyRequestsTable rows={mine} onView={(id) => openDetail(id, "view")} onEdit={editDraft} onSubmit={submitDraft} acting={acting} />
        </section>
      </div>
    )}

    {tab === "approvals" && (
      <div className="reimbursement-tab-body">
        <section className="reimbursement-card">
          <h2>Approvals Assigned to You</h2>
          <ApprovalsTable rows={approvals} onView={(id) => openDetail(id, "view")} onReview={(id) => openDetail(id, "review")} canApprove={canApprove} />
        </section>
      </div>
    )}

    {tab === "verification" && showVerificationTab && (
      <div className="reimbursement-tab-body">
        <section className="reimbursement-card">
          <h2>Awaiting Finance Verification</h2>
          <p className="reimbursement-hint">Approved by the Project Owner, pending Finance verification before payment.</p>
          <VerificationTable rows={verification} onView={(id) => openDetail(id, "review")} />
        </section>
      </div>
    )}

    {tab === "process" && (
      <div className="reimbursement-tab-body">
        <section className="reimbursement-card">
          <h2>Requests Awaiting Processing or Payment</h2>
          <ProcessingTable rows={processing} onView={(id) => openDetail(id, "view")} onReview={(id) => openDetail(id, "review")} />
        </section>
      </div>
    )}

    {tab === "approved" && (
      <div className="reimbursement-tab-body">
        <section className="reimbursement-card">
          <h2>{canProcess && !canApprove ? "Processed Requests" : "Approved by You"}</h2>
          <ApprovedTable rows={approvedByMe} onView={(id) => openDetail(id, "view")} />
        </section>
      </div>
    )}

    <DetailPanel
      detail={detail}
      loading={detailLoading}
      mode={detailMode}
      onClose={() => setDetail(null)}
      onEdit={editDraft}
      onSubmit={submitDraft}
      onReview={review}
      onApprove={approve}
      onVerify={verify}
      onRejectClick={() => { setRejectScope("owner"); setReturningForCorrection(false); setShowReject(true); }}
      onReturnClick={() => { setRejectScope("owner"); setReturningForCorrection(true); setShowReject(true); }}
      onFinanceRejectClick={() => { setRejectScope("finance"); setReturningForCorrection(false); setShowReject(true); }}
      onFinanceReturnClick={() => { setRejectScope("finance"); setReturningForCorrection(true); setShowReject(true); }}
      onProcess={process}
      onPay={pay}
      onDownload={downloadReceipt}
      acting={acting}
      canCreate={canCreate}
      canApprove={canApprove}
      canReject={canReject}
      canVerify={canVerify}
      canProcess={canProcess}
      formatAmount={formatAmount}
      formatDateTime={formatDateTime}
    />

    {showReject && detail && (
      <div className="reimbursement-modal-backdrop" onClick={() => setShowReject(false)}>
        <div className="reimbursement-modal" onClick={(e) => e.stopPropagation()}>
          <h3>{returningForCorrection ? (rejectScope === "finance" ? "Return for Correction (Finance)" : "Return for Correction") : (rejectScope === "finance" ? "Reject Request (Finance)" : "Reject Request")}</h3>
          <p>The employee will be notified. {returningForCorrection ? "A correction reason is mandatory." : "A rejection reason is mandatory."}</p>
          <textarea rows="4" maxLength="1000" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={returningForCorrection ? "What must the employee correct?" : "Why is this being rejected?"} />
          <div className="reimbursement-modal-actions">
            <button type="button" className="reimbursement-secondary" onClick={() => { setShowReject(false); setReturningForCorrection(false); }}>Cancel</button>
            <button type="button" className={returningForCorrection ? "reimbursement-approve" : "reimbursement-danger"} disabled={acting} onClick={returningForCorrection ? returnForCorrection : reject}>{acting ? "Saving…" : returningForCorrection ? "Return for Correction" : "Reject"}</button>
          </div>
        </div>
      </div>
    )}
  </main>;
}

function ReimbursementDashboard({ summary, filters, onApply, onExport }) {
  const [draft, setDraft] = useState(filters);
  useEffect(() => setDraft(filters), [filters]);
  const set = (key, value) => setDraft((old) => ({ ...old, [key]: value }));
  const options = summary?.options || {};
  const statuses = summary?.statusDistribution || [];
  const groups = summary?.expenseGroupDistribution || [];
  const palette = ["#0f766e", "#2563eb", "#f59e0b", "#9333ea", "#dc2626", "#0891b2", "#64748b", "#db2777"];
  const donut = (items, valueKey) => {
    const total = items.reduce((sum, item) => sum + Number(item[valueKey] || 0), 0);
    let cursor = 0;
    const slices = items.map((item, i) => { const start = cursor; cursor += total ? (Number(item[valueKey] || 0) / total) * 100 : 0; return `${palette[i % palette.length]} ${start}% ${cursor}%`; });
    return { total, style: { background: total ? `conic-gradient(${slices.join(",")})` : "#e2e8f0" } };
  };
  const renderDonut = (title, items, valueKey, amount = false) => {
    const result = donut(items, valueKey);
    return <div className="reimbursement-chart-card"><h3>{title}</h3><div className="reimbursement-donut-row"><div className="reimbursement-donut" style={result.style}><span>{amount ? `₹${formatAmount(result.total)}` : result.total}</span></div><div className="reimbursement-legend">{items.map((item, i) => <div key={item.label}><i style={{ background: palette[i % palette.length] }} /><span>{item.label}</span><b>{amount ? `₹${formatAmount(item[valueKey])}` : item.count}</b></div>)}</div></div></div>;
  };
const dateRange = draft.dateRange || "Overall";
  // The dashboard API returns the actual per-month trend (respecting all
  // applied filters). Start from the first month that has data onward so the
  // full 6 months are NOT forced when older data is unavailable, while zero
  // record months that fall inside the data range are always kept.
  const rawTrend = summary?.trend || [];
  const hasTrendData = (item) => Number(item?.COR?.count) > 0 || Number(item?.SGA?.count) > 0
    || Number(item?.COR?.amount) > 0 || Number(item?.SGA?.amount) > 0;
  const firstDataIndex = rawTrend.findIndex(hasTrendData);
  const trend = firstDataIndex === -1 ? [] : rawTrend.slice(firstDataIndex).slice(-6);
  const monthTotal = (item) => ({
    count: (Number(item?.COR?.count) || 0) + (Number(item?.SGA?.count) || 0),
    amount: (Number(item?.COR?.amount) || 0) + (Number(item?.SGA?.amount) || 0),
  });
  const maxTrendCount = Math.max(1, ...trend.flatMap((item) => [item?.COR?.count || 0, item?.SGA?.count || 0, monthTotal(item).count]));
  const maxTrendAmount = Math.max(1, ...trend.flatMap((item) => [item?.COR?.amount || 0, item?.SGA?.amount || 0, monthTotal(item).amount]));
  return <section className="reimbursement-card reimbursement-dashboard">
    <div className="reimbursement-section-head"><div><h2>Reimbursement Dashboard</h2><p>Summary, trends, and filtered report within your access scope.</p></div><button type="button" className="reimbursement-secondary" onClick={onExport}>Export Filtered Excel</button></div>
    <div className="reimbursement-report-filters">
      <label>Date Range<select value={dateRange} onChange={(e) => set("dateRange", e.target.value)}>{["Overall", "Week", "Month", "30 Days", "90 Days", "Specific Date", "Custom"].map((v) => <option key={v}>{v}</option>)}</select></label>
      <label>Based On<select value={draft.basedOn || "submitted"} onChange={(e) => set("basedOn", e.target.value)}><option value="submitted">Submitted Date</option><option value="expense">Expense Date</option><option value="invoice">Invoice Date</option></select></label>
      {dateRange === "Specific Date" && <label>Specific Date<input type="date" value={draft.specificDate || ""} onChange={(e) => set("specificDate", e.target.value)} /></label>}
      {dateRange === "Custom" && <><label>From<input type="date" value={draft.fromDate || ""} onChange={(e) => set("fromDate", e.target.value)} /></label><label>To<input type="date" value={draft.toDate || ""} onChange={(e) => set("toDate", e.target.value)} /></label></>}
      <label>Expense Department<select value={draft.expenseDepartment || ""} onChange={(e) => set("expenseDepartment", e.target.value)}><option value="">All</option>{(options.expenseDepartments || []).map((v) => <option key={v}>{v}</option>)}</select></label>
      <label>Project<select value={draft.projectId || ""} onChange={(e) => set("projectId", e.target.value)}><option value="">All</option>{(options.projects || []).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}</select></label>
      <label>Status<select value={draft.status || ""} onChange={(e) => set("status", e.target.value)}><option value="">All</option>{(options.statuses || []).map((v) => <option key={v}>{v}</option>)}</select></label>
      <label>Expense Group<select value={draft.expenseGroup || ""} onChange={(e) => set("expenseGroup", e.target.value)}><option value="">All</option>{(options.expenseGroups || []).map((v) => <option key={v}>{v}</option>)}</select></label>
      <label>Nature<select value={draft.nature || ""} onChange={(e) => set("nature", e.target.value)}><option value="">All</option>{(options.natures || []).map((v) => <option key={v}>{v}</option>)}</select></label>
      <label>Employee<select value={draft.employeeId || ""} onChange={(e) => set("employeeId", e.target.value)}><option value="">All</option>{(options.employees || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></label>
      <div className="reimbursement-filter-actions"><button type="button" className="reimbursement-submit" onClick={() => onApply(draft)}>Apply</button><button type="button" className="reimbursement-secondary" onClick={() => onApply({ dateRange: "Overall", basedOn: "submitted" })}>Reset</button></div>
    </div>
    {!summary ? <p className="reimbursement-state">Loading reimbursement summary…</p> : <>
      <div className="reimbursement-kpis reimbursement-kpis--five"><div><span>Total Requests</span><strong>{summary.totalRequests}</strong></div><div><span>Total Amount</span><strong>₹{formatAmount(summary.totalAmount)}</strong></div><div><span>Committed Amount</span><strong>₹{formatAmount(summary.committedAmount)}</strong></div><div><span>Pending Approval</span><strong>{summary.pendingApproval}</strong></div><div><span>Pending Payment</span><strong>{summary.pendingPayment}</strong></div></div>
      <div className="reimbursement-charts">{renderDonut("Status Distribution", statuses, "count")}{renderDonut("Expense Group Distribution", groups, "amount", true)}</div>
      <div className="reimbursement-chart-card reimbursement-trend"><h3>Request + Amount Trend{trend.length ? ` (${trend.length} month${trend.length === 1 ? "" : "s"})` : ""}</h3><div className="reimbursement-trend-legend reimbursement-trend-legend--categories"><span className="reimbursement-trend-legend-cor">COR</span><span className="reimbursement-trend-legend-sga">SGA</span><span className="reimbursement-trend-legend-total">Total</span><span>Bar lengths scale separately for requests and amounts</span></div>{!trend.length ? <p className="reimbursement-state">No reimbursement data available for this period.</p> : <div className="reimbursement-horizontal-trend">{trend.map((month) => <section className="reimbursement-horizontal-trend-month" key={month.monthKey || month.month} aria-label={`${month.month} request and amount trend`}><h4>{month.month}</h4>{["COR", "SGA", "TOTAL"].map((category) => { const isTotal = category === "TOTAL"; const value = isTotal ? monthTotal(month) : (month[category] || { count: 0, amount: 0 }); const countWidth = `${value.count ? Math.max(2, value.count / maxTrendCount * 100) : 0}%`; const amountWidth = `${value.amount ? Math.max(2, value.amount / maxTrendAmount * 100) : 0}%`; return <div className={`reimbursement-horizontal-trend-category reimbursement-horizontal-trend-category--${category.toLowerCase()}`} key={category}><strong>{isTotal ? "Total" : category}</strong><div className="reimbursement-horizontal-trend-metric"><span>Requests <b>{value.count}</b></span><div className="reimbursement-horizontal-trend-track" title={`${month.month} ${isTotal ? "Total" : category}: ${value.count} requests`}><i style={{ width: countWidth }} /></div></div><div className="reimbursement-horizontal-trend-metric"><span>Amount <b>₹{formatAmount(value.amount)}</b></span><div className="reimbursement-horizontal-trend-track" title={`${month.month} ${isTotal ? "Total" : category}: ₹${formatAmount(value.amount)}`}><i style={{ width: amountWidth }} /></div></div></div>; })}</section>)}</div>}</div>
      <div className="reimbursement-table-wrap"><table className="reimbursement-table"><thead><tr><th scope="col">Status</th><th scope="col">Request Count</th><th scope="col">Total Amount</th></tr></thead><tbody>{Object.entries(summary.statuses || {}).map(([status, value]) => <tr key={status}><td><StatusBadge status={status} /></td><td>{value.count}</td><td>₹{formatAmount(value.amount)}</td></tr>)}</tbody></table></div>
      {!!(summary.projectDistribution || []).length && <>
        <h3 className="reimbursement-report-records-title">Project Distribution (Committed = Approved + Processed + Paid)</h3>
        <div className="reimbursement-table-wrap"><table className="reimbursement-table"><thead><tr><th scope="col">Project</th><th scope="col">Request Count</th><th scope="col">Total Amount</th><th scope="col">Committed Amount</th></tr></thead><tbody>{(summary.projectDistribution || []).map((p) => <tr key={p.project}><td>{p.project}</td><td>{p.count}</td><td>₹{formatAmount(p.amount)}</td><td>₹{formatAmount(p.committed)}</td></tr>)}</tbody></table></div>
      </>}
      <h3 className="reimbursement-report-records-title">Filtered Report Records</h3>
      <div className="reimbursement-table-wrap"><table className="reimbursement-table"><thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Expense Department</th><th scope="col">Project</th><th scope="col">Status</th><th scope="col">Amount</th><th scope="col">Submitted</th><th scope="col">Remarks</th></tr></thead><tbody>{(summary.records || []).map((row) => <tr key={row.id}><td>#{row.id}</td><td>{row.employeeName} ({row.employeeId})</td><td>{row.expenseDepartment}</td><td>{row.project}</td><td><StatusBadge status={row.status} /></td><td>₹{formatAmount(row.amount)}</td><td>{row.submittedAt || row.createdAt || "—"}</td><td>{row.remarks}</td></tr>)}</tbody></table></div>
    </>}
  </section>;
}

function MyRequestsTable({ rows, onView, onEdit, onSubmit, acting }) {
  if (!rows?.length) return <p className="reimbursement-state">You have not raised any reimbursements yet.</p>;
  return (
    <div className="reimbursement-table-wrap">
      <table className="reimbursement-table">
        <thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Department of Expense</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Total Amount</th><th scope="col">Submitted On</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>#{row.id}</td>
              <td>{row.employeeName || "—"}</td>
              <td>{row.departmentOfExpense || "—"}</td>
              <td>{row.expenseNatures || "—"}</td>
              <td>{row.expenseGroups || "—"}</td>
              <td>₹{formatAmount(row.totalAmount)}</td>
              <td>{formatDateTime(row.submittedAt)}</td>
              <td><StatusBadge status={row.businessStatus || row.status} />{row.status === "RETURNED_FOR_CORRECTION" && row.lastRemarks && <div className="reimbursement-row-remarks">{row.lastRemarks}</div>}</td>
              <td className="reimbursement-row-actions">
                <button type="button" onClick={() => onView(row.id)}>View</button>
                {["DRAFT", "RETURNED_FOR_CORRECTION"].includes(row.status) && <button type="button" onClick={() => onEdit(row.id)}>Edit</button>}
                {row.status === "DRAFT" && <button type="button" className="reimbursement-submit" disabled={acting} onClick={() => onSubmit(row.id)}>Submit</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApprovalsTable({ rows, onView, onReview, canApprove }) {
  if (!rows?.length) return <p className="reimbursement-state">No requests are waiting for your approval.</p>;
  return (
    <div className="reimbursement-table-wrap">
      <table className="reimbursement-table">
        <thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Department of Expense</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Total Amount</th><th scope="col">Submitted On</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {rows.map((row) => {
            // Every row here is already assigned to this user as Project Owner
            // (the queue filters on approver_id), so Review is offered only for
            // the Project Owner stage and when the user holds the permission.
            // The backend still authorizes the transition.
            const isAssignedApprover = CLAIMABLE.has(row.status);
            return (
              <tr key={row.id}>
                <td>#{row.id}</td>
                <td>{row.employeeName || "—"}</td>
                <td>{row.departmentOfExpense || "—"}</td>
                <td>{row.expenseNatures || "—"}</td>
                <td>{row.expenseGroups || "—"}</td>
                <td>₹{formatAmount(row.totalAmount)}</td>
                <td>{formatDateTime(row.submittedAt)}</td>
                <td><StatusBadge status={row.businessStatus || row.status} /></td>
                <td className="reimbursement-row-actions">
                  <button type="button" onClick={() => onView(row.id)}>View</button>
                  {canApprove && isAssignedApprover && <button type="button" className="reimbursement-submit" onClick={() => onReview(row.id)}>Review</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VerificationTable({ rows, onView }) {
  if (!rows?.length) return <p className="reimbursement-state">No requests are awaiting Finance verification.</p>;
  return (
    <div className="reimbursement-table-wrap">
      <table className="reimbursement-table">
        <thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Department of Expense</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Total Amount</th><th scope="col">Submitted On</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>#{row.id}</td>
              <td>{row.employeeName || "—"}</td>
              <td>{row.departmentOfExpense || "—"}</td>
              <td>{row.expenseNatures || "—"}</td>
              <td>{row.expenseGroups || "—"}</td>
              <td>₹{formatAmount(row.totalAmount)}</td>
              <td>{formatDateTime(row.submittedAt)}</td>
              <td><StatusBadge status={row.businessStatus || row.status} /></td>
              <td className="reimbursement-row-actions">
                <button type="button" onClick={() => onView(row.id)}>Verify</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApprovedTable({ rows, onView }) {
  if (!rows?.length) return <p className="reimbursement-state">You have not approved any reimbursements yet.</p>;
  return (
    <div className="reimbursement-table-wrap">
      <table className="reimbursement-table">
        <thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Department</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Total Amount</th><th scope="col">Submitted On</th><th scope="col">Approved On</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>#{row.id}</td>
              <td>{row.employeeName || "—"}</td>
              <td>{row.departmentOfExpense || "—"}</td>
              <td>{row.expenseNatures || "—"}</td>
              <td>{row.expenseGroups || "—"}</td>
              <td>₹{formatAmount(row.totalAmount)}</td>
              <td>{formatDateTime(row.submittedAt)}</td>
              <td>{formatDateTime(row.approvedAt)}</td>
              <td><StatusBadge status={row.businessStatus || row.status} /></td>
              <td className="reimbursement-row-actions">
                <button type="button" onClick={() => onView(row.id)}>View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProcessingTable({ rows, onView, onReview }) {
  if (!rows?.length) return <p className="reimbursement-state">No approved reimbursements are awaiting processing or payment.</p>;
  return (
    <div className="reimbursement-table-wrap">
      <table className="reimbursement-table">
        <thead><tr><th scope="col">Request ID</th><th scope="col">Employee</th><th scope="col">Department of Expense</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Total Amount</th><th scope="col">Submitted On</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>#{row.id}</td>
              <td>{row.employeeName || "—"}</td>
              <td>{row.departmentOfExpense || "—"}</td>
              <td>{row.expenseNatures || "—"}</td>
              <td>{row.expenseGroups || "—"}</td>
              <td>₹{formatAmount(row.totalAmount)}</td>
              <td>{formatDateTime(row.submittedAt)}</td>
              <td><StatusBadge status={row.businessStatus || row.status} /></td>
              <td className="reimbursement-row-actions">
                <button type="button" onClick={() => onView(row.id)}>View</button>
                <button type="button" className="reimbursement-submit" onClick={() => onReview(row.id)}>{row.status === "PROCESSED" ? "Mark as Paid" : "Process"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailPanel({ detail, loading, mode = "view", onClose, onEdit, onSubmit, onReview, onApprove, onVerify, onRejectClick, onReturnClick, onFinanceRejectClick, onFinanceReturnClick, onProcess, onPay, onDownload, acting, canCreate, canApprove, canReject, canVerify, canProcess, formatAmount, formatDateTime }) {
  if (!detail && !loading) return null;
  return (
    <div className="reimbursement-modal-backdrop" onClick={onClose}>
      <div className="reimbursement-detail" onClick={(e) => e.stopPropagation()}>
        <div className="reimbursement-detail-head">
          <div>
            <h3>Reimbursement #{detail?.id}</h3>
            {detail && <StatusBadge status={detail.businessStatus || detail.status} />}
            {detail && <p className="reimbursement-detail-meta">Submitted on {formatDateTime(detail.submittedAt)}</p>}
          </div>
          <button type="button" className="reimbursement-modal-close" onClick={onClose}>×</button>
        </div>

        {loading || !detail ? <p className="reimbursement-state">Loading request…</p> : <>
          {(() => {
            const expenseLines = toLineArray(detail.lines).map((line) => ({ ...line, amount: parseAmount(line.amount) }));
            const totalAmount = expenseLines.reduce((sum, line) => sum + parseAmount(line.amount), 0);
            const hasProject = Boolean(detail.project_code || detail.project_id);
            return <>
              <h4 className="reimbursement-detail-section">Employee Details</h4>
              <div className="reimbursement-grid">
                {[["Emp ID", detail.employee_id], ["Emp Name", detail.employee_name], ["Company", detail.company], ["BU", detail.business_unit], ["Division", detail.division], ["Department", detail.department]].map(([label, value]) => (
                  <label key={label}>{label}<input readOnly value={value || "—"} /></label>
                ))}
              </div>

              <h4 className="reimbursement-detail-section">Expense Details</h4>
              <div className="reimbursement-grid">
                <label>Department of Expense<input readOnly value={detail.project_nature || "—"} /></label>
              </div>
              <div className="reimbursement-table-wrap">
                <table className="reimbursement-table reimbursement-table--lines">
                  <thead><tr><th scope="col">Expense Code</th><th scope="col">Expense Nature</th><th scope="col">Expense Group</th><th scope="col">Expense Date</th><th scope="col">Amount</th><th scope="col">Invoice No</th><th scope="col">Invoice Date</th><th scope="col">Attachments</th></tr></thead>
                  <tbody>
                    {expenseLines.map((line, i) => (
                      <tr key={line.id ?? i + 1}>
                        <td>{line.expenseCode}</td>
                        <td>{line.natureName}</td>
                        <td>{line.groupName}</td>
                        <td>{line.expenseDate}</td>
                        <td>₹{formatAmount(line.amount)}</td>
                        <td>{line.invoiceNumber || "—"}</td>
                        <td>{line.invoiceDate || "—"}</td>
                        <td>
                          {line.attachments?.length ? line.attachments.map((a, ai) => (
                            <button key={a.id} type="button" onClick={() => onDownload(a.id, a.name)} title={a.name}>View{line.attachments.length > 1 ? ` ${ai + 1}` : ""}</button>
                          )) : line.attachment ? (
                            <button type="button" onClick={() => onDownload(line.attachment.id, line.attachment.name)} title={line.attachment.name}>View</button>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan="4">Total</td><td>₹{formatAmount(totalAmount)}</td><td colSpan="3" /></tr>
                  </tfoot>
                </table>
              </div>

              {hasProject && <>
                <h4 className="reimbursement-detail-section">Project Details</h4>
                <div className="reimbursement-grid">
                  {[["Project", detail.project_code], ["Customer", detail.customer], ["Project Description", detail.project_description], ["Approver / Project Owner", detail.approver_name]].map(([label, value]) => (
                    <label key={label}>{label}<input readOnly value={value || "—"} /></label>
                  ))}
                </div>
              </>}
            </>;
          })()}

          <h4 className="reimbursement-detail-section">Approval History</h4>
          {!detail.history?.length ? <p className="reimbursement-state">No history yet.</p> : (
            <ol className="reimbursement-timeline">
              {detail.history.map((entry) => (
                <li key={entry.id}>
                  <div className="reimbursement-timeline-main">
                    <strong>{entry.fromStatus ? `${STATUS_LABELS[entry.fromStatus] || entry.fromStatus} → ${STATUS_LABELS[entry.toStatus] || entry.toStatus}` : STATUS_LABELS[entry.toStatus] || entry.toStatus}</strong>
                    <span className="reimbursement-timeline-meta">by {entry.actorName} · {formatDateTime(entry.createdAt)}</span>
                  </div>
                  {entry.remarks && <p className="reimbursement-timeline-remarks">{entry.remarks}</p>}
                </li>
              ))}
            </ol>
          )}

          {mode === "review" && (
            <div className="reimbursement-detail-actions">
              {detail.status === "DRAFT" && canCreate && <>
                <button type="button" onClick={() => onEdit(detail.id)}>Edit</button>
                <button type="button" className="reimbursement-submit" disabled={acting} onClick={() => onSubmit(detail.id)}>Submit for Approval</button>
              </>}
              {CLAIMABLE.has(detail.status) && detail.isApprover && <>
                {detail.status === "SUBMITTED" && canApprove && <button type="button" className="reimbursement-secondary" disabled={acting} onClick={onReview}>Begin Review</button>}
                {canApprove && <button type="button" className="reimbursement-approve" disabled={acting} onClick={onApprove}>Approve &amp; Send to Finance</button>}
                {canReject && <button type="button" className="reimbursement-secondary" disabled={acting} onClick={onReturnClick}>Return for Correction</button>}
                {canReject && <button type="button" className="reimbursement-danger" disabled={acting} onClick={onRejectClick}>Reject</button>}
              </>}
              {FINANCE_CLAIMABLE.has(detail.status) && canVerify && <>
                <button type="button" className="reimbursement-approve" disabled={acting} onClick={onVerify}>Verify for Payment</button>
                <button type="button" className="reimbursement-secondary" disabled={acting} onClick={onFinanceReturnClick}>Return for Correction</button>
                <button type="button" className="reimbursement-danger" disabled={acting} onClick={onFinanceRejectClick}>Reject</button>
              </>}
              {detail.status === "APPROVED" && canProcess && <button type="button" className="reimbursement-process" disabled={acting} onClick={onProcess}>Mark as Processed</button>}
              {detail.status === "PROCESSED" && canProcess && <button type="button" className="reimbursement-process" disabled={acting} onClick={onPay}>Mark as Paid</button>}
            </div>
          )}
        </>}
      </div>
    </div>
  );
}
